import { Raffle } from '@/app/entities/Raffle'
import { withCdn } from '@/app/utils/withCdn'
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { raffleHasEnded } from '../utils'

const columnHelper = createColumnHelper<Raffle>()

const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: () => <div className="pl-3 text-left sm:pl-6 md:pl-8">Sorteio</div>,
    cell: ({ row }) => (
      <div className="py-3 pl-3 text-left sm:py-4 sm:pl-6 md:py-5 md:pl-8">
        <div className="flex items-center gap-2 text-sm font-semibold sm:gap-3 md:gap-5">
          <div
            className="h-7 w-5 flex-shrink-0 rounded-md bg-gray-200 sm:h-8 sm:w-6 md:h-9 md:w-7"
            style={{
              aspectRatio: '1179 / 1600',
              backgroundImage: `url(${withCdn(row.original?.banner)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-gray-900 sm:text-base">
              {row.original.name}
            </div>
            <div className="mt-1 flex flex-col gap-1 text-xs text-gray-500 md:hidden">
              <span>
                {raffleHasEnded(row.original) ? (
                  <span className="text-[#9F9F9F]">Finalizado</span>
                ) : (
                  <span className="text-blue-500">Em andamento</span>
                )}
              </span>
              {row.original?.winners?.length > 0 ? (
                <span>
                  {row.original.winners.length} cupom{row.original.winners.length > 1 ? 's' : ''} sorteado{row.original.winners.length > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-[#9F9F9F]">Não sorteado</span>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
  }),
  columnHelper.accessor('endDate', {
    id: 'status',
    header: () => <div className="hidden pl-6 text-left md:block md:pl-8">Status</div>,
    cell: ({ row }) => {
      const hasEnded = raffleHasEnded(row.original)
      return (
        <div className="hidden py-5 pl-6 md:block md:pl-8">
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
            hasEnded 
              ? 'bg-gray-100 text-[#9F9F9F]' 
              : 'bg-blue-100 text-blue-600'
          }`}>
            {hasEnded ? 'Finalizado' : 'Em andamento'}
          </span>
        </div>
      )
    },
  }),
  columnHelper.accessor('winners.code', {
    id: 'code',
    header: () => <div className="hidden pl-6 text-left md:block md:pl-8">Cupons Sorteados</div>,
    cell: ({ row }) => {
      const hasWinners = row.original?.winners?.length > 0
      const firstThreeWinners = row.original?.winners?.slice(0, 3)
      const hasMoreThanThreeWinners = row.original?.winners?.length > 3
      return (
        <div className="hidden items-center gap-2 py-5 pl-6 md:flex md:pl-8">
          {hasWinners ? (
            <div className="flex flex-wrap items-center gap-2">
              {firstThreeWinners?.map((winner, index) => (
                <div
                  key={winner?.code || index}
                  className="rounded-md border bg-[#E5E5E5] px-2 py-1 text-xs font-semibold"
                >
                  {winner.code}
                </div>
              ))}
              {hasMoreThanThreeWinners && (
                <div className="text-xs font-semibold text-gray-500">
                  +{row.original?.winners?.length - 3}
                </div>
              )}
            </div>
          ) : (
            <div className="font-semibold text-[#9F9F9F]">Não sorteado</div>
          )}
        </div>
      )
    },
  }),
]

interface UseRaffleAdminTableProps {
  data: Raffle[]
}

export default function useRaffleAdminTable({
  data,
}: UseRaffleAdminTableProps) {
  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return table
}
